// ─────────────────────────────────────────────────────────────────────────────
// Traduce los errores de POST /packages (creación manual de un pedido), de
// POST /packages/bulk (carga masiva) y de la verificación de correos de
// vendedores de la planilla, a algo que el comercio pueda corregir: qué dato
// falla, en qué paso del wizard (o en qué fila del Excel) está y por qué.
//
// Formas que devuelve el backend (microservice-packages, el BFF las reenvía intactas):
//  - Validación del DTO: 400 { message: string[] } con la ruta del campo como prefijo,
//    en inglés ("shipping_address.city.name should not be empty"). Los mensajes custom
//    llegan con la ruta pegada ("cashOnDelivery.El monto a cobrar debe ser…").
//  - Lo mismo en el lote, con el índice del paquete delante: cada ítem arranca con
//    "packages.N.", donde N es el índice 0-based del array enviado.
//  - Reglas de negocio: 4xx { message: string } ya en español (contrato, operador…).
//  - Número de venta repetido: 400 { code: "DUPLICATE_ORDER_ID", order_id }.
//  - Cualquier otra excepción: 500 { message: "Internal server error" }, sin detalle.
//  - Timeout / sin respuesta: el error de axios no trae `response`.
// ─────────────────────────────────────────────────────────────────────────────

import { apiBodyMessage, apiBodyMessages, apiErrorMessage, apiErrorStatus } from "./apiErrorMessage";

const RECIPIENT_STEP = 2;
const PACKAGE_STEP = 3;

// Primera fila de DATOS de la plantilla de carga masiva: las filas 1-2 son los
// encabezados. El backend numera los paquetes desde 0 (`packages.0`), así que
// `packages.N` es la fila N + 3 de Excel.
const FIRST_DATA_ROW = 3;

// Ruta del DTO → campo del formulario del wizard. `name: null` = el dato no lo
// escribe el usuario (lo arma el front), así que no hay campo que resaltar.
const FIELD_BY_PATH = {
  receiver_name: { name: "receiver_name", step: RECIPIENT_STEP, label: "Nombre del destinatario" },
  receiver_phone: { name: "receiver_phone", step: RECIPIENT_STEP, label: "Teléfono del destinatario" },
  "shipping_address.street_name": { name: "address_line", step: RECIPIENT_STEP, label: "Dirección de entrega" },
  // `labelKey`: la división administrativa cambia de nombre por país (Región/Provincia,
  // Comuna/Localidad); se usa la misma etiqueta que muestra RecipientStep.
  "shipping_address.city": { name: "regionCode", step: RECIPIENT_STEP, label: "Región de entrega", labelKey: "region" },
  "shipping_address.municipality": { name: "municipality", step: RECIPIENT_STEP, label: "Comuna de entrega", labelKey: "commune" },
  "shipping_address.zip_code": { name: "zip_code", step: RECIPIENT_STEP, label: "Código postal" },
  "shipping_address.country": { name: null, step: null, label: "País de entrega" },
  shipping_address: { name: null, step: RECIPIENT_STEP, label: "Dirección de entrega" },
  order_id: { name: "order_id", step: PACKAGE_STEP, label: "Número de venta" },
  "packageDimensions.weight": { name: "package_weight", step: PACKAGE_STEP, label: "Peso (kg)" },
  "packageDimensions.length": { name: "package_length", step: PACKAGE_STEP, label: "Largo (cm)" },
  "packageDimensions.width": { name: "package_width", step: PACKAGE_STEP, label: "Ancho (cm)" },
  "packageDimensions.height": { name: "package_height", step: PACKAGE_STEP, label: "Alto (cm)" },
  packageDimensions: { name: null, step: PACKAGE_STEP, label: "Dimensiones del paquete" },
  cashOnDelivery: { name: "cod_amount", step: PACKAGE_STEP, label: "Monto a cobrar" },
  // Campo que sólo existe en la carga masiva (RequestCreatePackagesDto de
  // `packages/bulk`), no en el alta manual. `name: null`: no hay campo del
  // formulario que resaltar, el dato se corrige en la fila del Excel.
  additionalLabels: { name: null, step: PACKAGE_STEP, label: "Cantidad de bultos" },
};

// `sellerEmail` NO va en FIELD_BY_PATH aunque sea de la carga masiva: `resolveField`
// lo comparte el alta manual, y ahí esa ruta es desconocida a propósito — se
// muestra cruda ("sellerEmail: Debe ser texto.") porque el campo no existe en el
// formulario, y hay un test que lo fija. Mapearlo acá cambiaría ese resultado sin
// arreglar nada del alta manual (que nunca manda `sellerEmail`).
const BULK_FIELD_BY_PATH = {
  ...FIELD_BY_PATH,
  sellerEmail: { name: null, step: null, label: "Correo del vendedor" },
};

// Busca la ruta conocida más específica que contenga a `path`
// ("shipping_address.city.name" → "shipping_address.city").
const resolveInFieldMap = (map, path) => {
  let candidate = path;
  while (candidate) {
    if (map[candidate]) return map[candidate];
    const cut = candidate.lastIndexOf(".");
    candidate = cut === -1 ? "" : candidate.slice(0, cut);
  }
  return null;
};

const resolveField = (path) => resolveInFieldMap(FIELD_BY_PATH, path);

// Restricciones por defecto de class-validator → texto en español.
const translateConstraint = (constraint, t) => {
  if (/^should not be empty$/.test(constraint) || /^must be a non-empty object$/.test(constraint)) {
    return t("Este dato es obligatorio.");
  }
  if (/^must be a string$/.test(constraint)) return t("Debe ser texto.");
  if (/^must be a number/.test(constraint)) return t("Debe ser un número.");
  if (/^must be an integer number$/.test(constraint)) return t("Debe ser un número entero.");
  if (/^must be one of the following values/.test(constraint)) return t("Tiene un valor no permitido.");
  if (/^must be either object or array$/.test(constraint)) return t("Tiene un formato inválido.");
  const maxLength = constraint.match(/^must be shorter than or equal to (\d+) characters$/);
  if (maxLength) return t("Admite como máximo {{max}} caracteres.", { max: maxLength[1] });
  const min = constraint.match(/^must not be less than (-?[\d.]+)$/);
  if (min) return t("No puede ser menor que {{min}}.", { min: min[1] });
  return constraint;
};

const PATH = "[A-Za-z_]\\w*(?:\\.[A-Za-z_]\\w*)*";
// "shipping_address.nested property city must be either object or array"
const NESTED_RE = new RegExp(`^(?:(${PATH})\\.)?nested property (\\w+) (must be either object or array)$`);
// "shipping_address.city.name should not be empty"
const DEFAULT_RE = new RegExp(`^(${PATH}) ((?:should|must) .+)$`);
// "cashOnDelivery.El monto a cobrar debe ser mayor a cero." (mensaje custom del DTO)
const CUSTOM_RE = new RegExp(`^(${PATH})\\.([^\\s.].*)$`);

// Un ítem del 400 de validación → { path, text } o null si no tiene la forma esperada.
const parseValidationItem = (raw, t) => {
  const nested = raw.match(NESTED_RE);
  if (nested) {
    const path = nested[1] ? `${nested[1]}.${nested[2]}` : nested[2];
    return { path, text: translateConstraint(nested[3], t) };
  }
  const standard = raw.match(DEFAULT_RE);
  if (standard) return { path: standard[1], text: translateConstraint(standard[2], t) };
  const custom = raw.match(CUSTOM_RE);
  if (custom && /^[A-ZÁÉÍÓÚÑ¿¡]/.test(custom[2])) return { path: custom[1], text: custom[2] };
  return null;
};

const CONFIG_ERROR_RE = /contrato|compañía logística|logisticsCompanyId|dirección de operaciones|companyId/i;
const COD_CURRENCY_RE = /^No se puede registrar un monto a cobrar/;

const isTimeout = (original) =>
  original?.code === "ECONNABORTED" || /timeout/i.test(original?.message || "");

/**
 * @param {object|null|undefined} errorInfo  El `error` que devuelve `createPackage`
 *   (salida de errorHandler.handleApiError): { message, status, data, originalError }.
 * @param {(key: string, opts?: object) => string} t  Traductor de i18next.
 * @param {{ region?: string, commune?: string }} [labels]  Nombres de las divisiones
 *   del país (`useCountry().labels`); sin ellos se usan "Región"/"Comuna".
 * @returns {{
 *   title: string,
 *   details: string[],
 *   fieldErrors: Array<{ name: string, message: string }>,
 *   targetStep: number,
 * }} `details` = una línea por problema, para el aviso del wizard. `fieldErrors` =
 *   errores a marcar en el formulario. `targetStep` = paso al que volver (el del
 *   primer campo con error; si no hay campo, el último paso con datos).
 */
export function describeCreatePackageError(errorInfo, t, labels) {
  const title = t("No pudimos crear el pedido");
  const details = [];
  const fieldErrors = [];
  const steps = [];

  const addFieldError = (field, text) => {
    const countryLabel = field?.labelKey ? labels?.[field.labelKey] : null;
    // Mismo texto que la etiqueta del campo en RecipientStep ("{región} de entrega").
    const label = countryLabel ? `${countryLabel} de entrega` : field ? t(field.label) : null;
    details.push(label ? `${label}: ${text}` : text);
    if (field?.name && !fieldErrors.some((f) => f.name === field.name)) {
      fieldErrors.push({ name: field.name, message: text });
    }
    if (field?.step) steps.push(field.step);
  };

  const status = errorInfo?.status ?? null;
  const data = errorInfo?.data ?? null;
  const serverMessage = data?.message;
  const original = errorInfo?.originalError;

  if (data?.code === "DUPLICATE_ORDER_ID") {
    const orderId = data.order_id ?? "";
    addFieldError(
      FIELD_BY_PATH.order_id,
      t('Ya existe un pedido con el número de venta "{{orderId}}". Usa otro número o deja el campo vacío.', { orderId })
    );
  } else if (Array.isArray(serverMessage)) {
    // class-validator informa todas las restricciones de un campo ("vacío" y "debe ser
    // texto"); con la primera basta para saber qué corregir.
    const seenPaths = new Set();
    serverMessage.forEach((raw) => {
      const item = typeof raw === "string" ? parseValidationItem(raw, t) : null;
      if (!item) {
        details.push(String(raw));
        return;
      }
      if (seenPaths.has(item.path)) return;
      seenPaths.add(item.path);
      const field = resolveField(item.path);
      // Ruta desconocida: se muestra tal cual, así el comercio (o soporte) sabe cuál es.
      addFieldError(field || { name: null, step: null, label: item.path }, item.text);
    });
  } else if (typeof serverMessage === "string" && COD_CURRENCY_RE.test(serverMessage)) {
    addFieldError(FIELD_BY_PATH.cashOnDelivery, serverMessage);
  } else if (typeof serverMessage === "string" && /^Concurrent request detected/i.test(serverMessage)) {
    details.push(t("Ya hay una creación en curso con estos mismos datos. Espera unos segundos y vuelve a intentar."));
  } else if (status === 500 || status === "500") {
    details.push(
      t("El servidor tuvo un error inesperado y no indicó qué dato falló. Intenta nuevamente; si persiste, contacta a soporte.")
    );
  } else if (
    typeof serverMessage === "string" &&
    serverMessage.trim() &&
    // Solo estos status traen texto de negocio en español. El 429 del throttler o el
    // 503 del BFF traen texto técnico en inglés: van al mensaje ya traducido de abajo.
    [400, 404, 409, 422].includes(Number(status))
  ) {
    details.push(serverMessage);
    if (CONFIG_ERROR_RE.test(serverMessage)) {
      details.push(t("No es un problema de los datos del pedido: depende de la configuración de tu cuenta. Contacta a tu operador logístico."));
    }
  } else if (Number(status) === 401) {
    // errorHandler deja el "Unauthorized" crudo del backend (o un texto de login).
    details.push(t("Tu sesión no pudo validarse al crear el pedido. Vuelve a iniciar sesión e intenta nuevamente."));
  } else if (!status && isTimeout(original)) {
    details.push(t("El servidor no respondió a tiempo. Revisa tu listado de pedidos antes de reintentar: el pedido pudo haberse creado."));
  } else {
    // 403/429/502/503, CORS o sin conexión: errorHandler ya dejó un mensaje traducido.
    const fallback = typeof errorInfo?.message === "string" && errorInfo.message.trim()
      ? errorInfo.message
      : t("Ha ocurrido un error inesperado");
    details.push(fallback);
  }

  if (details.length === 0) details.push(t("Ha ocurrido un error inesperado"));

  return {
    title,
    details,
    fieldErrors,
    targetStep: steps.length ? Math.min(...steps) : PACKAGE_STEP,
  };
}

// "packages.3.shipping_address.municipality.name should not be empty"
const BULK_ITEM_RE = /^packages\.(\d+)\.(.+)$/;

/**
 * Cada ítem del 400 de `packages/bulk` en una línea para el comercio, con la
 * FILA del Excel en la que está el dato (el backend cuenta paquetes desde 0:
 * `packages.N` = fila N + `FIRST_DATA_ROW`). Sin esa conversión el número que
 * vería el usuario no existe en su planilla.
 *
 * Los ítems que no tienen la forma esperada se muestran tal cual, recortados:
 * un texto raro es más útil que un aviso vacío. No hay `fieldErrors` porque el
 * dato no vive en el formulario del wizard, sino en una celda del archivo.
 *
 * @param {unknown[]} messages  Ítems de `message` del 400 (array del ValidationPipe).
 * @param {(key: string, opts?: object) => string} t  Traductor de i18next.
 * @returns {string[]} Una línea por problema, sin repetidos.
 */
export function describeBulkPackagesError(messages, t) {
  if (!Array.isArray(messages)) return [];

  const lines = [];
  const seenLines = new Set();
  // Como en describeCreatePackageError: el backend informa varias restricciones
  // del mismo campo ("vacío" y "muy largo"); con la primera basta para saber qué
  // corregir.
  const seenRowPaths = new Set();

  const pushLine = (line) => {
    const text = String(line).trim();
    if (!text || seenLines.has(text)) return;
    seenLines.add(text);
    lines.push(text);
  };

  messages.forEach((raw) => {
    const item = typeof raw === "string" ? raw : String(raw);
    const match = item.match(BULK_ITEM_RE);
    const parsed = match ? parseValidationItem(match[2], t) : null;
    if (!match || !parsed) {
      pushLine(item);
      return;
    }

    const row = Number(match[1]) + FIRST_DATA_ROW;
    const rowPath = `${row}.${parsed.path}`;
    if (seenRowPaths.has(rowPath)) return;
    seenRowPaths.add(rowPath);

    const field = resolveInFieldMap(BULK_FIELD_BY_PATH, parsed.path);
    // Ruta desconocida: se muestra tal cual, así el comercio (o soporte) sabe cuál es.
    const label = field ? t(field.label) : parsed.path;
    pushLine(t("Fila {{row}} – {{field}}: {{text}}", { row, field: label, text: parsed.text }));
  });

  return lines;
}

/**
 * Aviso del rechazo del lote: el título y las líneas que ve el comercio.
 *
 * Tres causas que NO se pueden mezclar, porque no se arreglan igual:
 *
 *  - Sin cuerpo del backend (`apiErrorStatus` null: timeout de 5 min, sin
 *    conexión, o la página HTML de un proxy) no hay NADA que mostrar, ni
 *    statusCode que leer. Peor: `insertPackages` no es transaccional, así que el
 *    lote pudo quedar creado —total o parcialmente— antes de perderse la
 *    respuesta. Por eso el título es NEUTRO ("no sabemos si se crearon") y no
 *    "no pudimos crearlos": afirmar que falló empuja a reintentar a ciegas, y
 *    reintentar un lote ya creado son pedidos duplicados.
 *  - Array de validación del DTO: se traduce a fila + campo del Excel.
 *  - Texto de negocio del backend: va tal cual (ya está en español).
 *
 * @param {object|null|undefined} error  El `error` que devuelve `createBulkPackages`.
 * @param {(key: string, opts?: object) => string} t  Traductor de i18next.
 * @returns {{title: string, details: string[]}}
 */
export function describeBulkRejection(error, t) {
  if (apiErrorStatus(error) === null) {
    return {
      title: t("No sabemos si se crearon los pedidos"),
      details: [
        t("No recibimos respuesta del servidor a tiempo. Revisa el listado de pedidos antes de reintentar: el lote pudo haberse creado."),
      ],
    };
  }

  return {
    title: t("No pudimos crear los pedidos"),
    details: Array.isArray(error?.message)
      ? describeBulkPackagesError(apiBodyMessages(error), t)
      : [apiErrorMessage(error, t("Error al cargar los paquetes."))],
  };
}

/**
 * Texto del rechazo de la verificación de correos de vendedores
 * (`POST /users/team/resolve-seller-emails`), o `null` si no hay ninguno que
 * mostrar.
 *
 * Vive acá, junto al resto de los textos de la carga masiva, y no en el
 * componente: el cuerpo del backend SÍ trae el motivo real ("Solo un supervisor
 * de ventas puede atribuir pedidos a otro usuario.") y hay que mostrarlo.
 * `resolveSellerEmails` devuelve un `{ message }` de RELLENO cuando la request
 * no llegó al servidor; sin `statusCode` no hay cuerpo del backend, y ahí lo que
 * corresponde decir es otra cosa (la conexión), que decide el componente.
 *
 * @param {object|null|undefined} error  El `error` que devuelve `resolveSellerEmails`.
 * @returns {string|null}
 */
export function sellerEmailsErrorText(error) {
  return apiErrorStatus(error) !== null ? apiBodyMessage(error) : null;
}
